import { ActivityType } from '@/components/Activities'
import { Attachment } from '@/modules/Upload/types'
import { ResponseArray, ResponseObject } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import queryString from 'query-string'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'
import {
    ContractOverviewTypes,
    ContractStatistical,
    CustomerContract,
    UpdateCustomerContractParams,
} from '../types'
import { RenewContractFormValues } from '@/modals/ContractModals/RenewContractModal'
import { TerminateContractFormValues } from '@/modals/ContractModals/TerminateContractModal'
import { SampleContract } from '../modules/Sample/types'

export const getContractCustomers = async (values: DataGridPayload) => {
    const response = await datacentral.get<ResponseArray<CustomerContract>>(
        `/contracts/customers?${queryString.stringify(values)}`
    )

    return response?.data
}

export const getContractCustomer = async ({
    contractID,
}: {
    contractID: number
}) => {
    const response = await datacentral.get<ResponseObject<CustomerContract>>(
        `/contracts/customers/${contractID}`
    )

    return response?.data
}

export const updateContractCustomer = async ({
    contractID,
    data,
    attachments,
    attachmentsToDelete,
    service_ids,
}: {
    contractID: number
    data: UpdateCustomerContractParams
    attachments: Attachment[]
    attachmentsToDelete?: number[]
    service_ids: number[]
}) => {
    const response = await datacentral.put(
        `/contracts/customers/${contractID}`,
        {
            contract: data,
            attachments: attachments,
            remove_attachments: attachmentsToDelete,
            service_ids: service_ids,
        }
    )

    return response?.data
}

export const getContractCustomersStatistical = async (
    payload: Omit<DataGridPayload, 'page' | 'page_size'>
): Promise<ResponseObject<ContractStatistical>> => {
    const response = await datacentral.get(
        `/contracts/customers/statistical?${queryString.stringify(payload)}`
    )
    return response?.data
}

export const getContractCustomerActivities = async (
    payload: Pick<DataGridPayload, 'page' | 'page_size'>
): Promise<ResponseArray<any>> => {
    const response = await datacentral.get<ResponseArray<ActivityType>>(
        `/contracts/customers/activities?${queryString.stringify(payload)}`
    )
    return response?.data
}

export const getContractCustomersOverview = async (
    payload: Omit<DataGridPayload, 'page' | 'page_size'>
): Promise<ResponseArray<any>> => {
    const response = await datacentral.get<ResponseArray<any>>(
        `/contracts/customers/overview?${queryString.stringify(payload)}`
    )
    return response?.data
}

export const getContractCustomersOverviewType = async (
    filters: string
): Promise<ResponseObject<ContractOverviewTypes>> => {
    const response = await datacentral.get(
        `/contracts/customers/overview/types?filters=${filters}`
    )
    return response?.data
}

export const getContractCustomerTypes = async ({
    values,
    status,
}: {
    values: DataGridPayload
    status: string
}) => {
    const response = await datacentral.get<ResponseArray<CustomerContract>>(
        `/contracts/customers/types?${queryString.stringify(values)}&contract_status=${status}`
    )

    return response?.data
}

export const cloneContractCustomer = async ({
    contractID,
    reason,
}: {
    contractID: number
    reason: string
}) => {
    const response = await datacentral.post<ResponseObject<CustomerContract>>(
        `/contracts/customers/${contractID}/retention`,
        { reason }
    )
    return response?.data
}

export const renewContractCustomer = async ({
    contractID,
    data,
}: {
    contractID: number
    data: RenewContractFormValues
}) => {
    const response = await datacentral.post<ResponseObject<CustomerContract>>(
        `/contracts/customers/${contractID}/renew`,
        data
    )
    return response?.data
}

export const terminateContractCustomer = async ({
    contractID,
    data,
}: {
    contractID: number
    data: TerminateContractFormValues
}) => {
    const response = await datacentral.post<ResponseObject<CustomerContract>>(
        `/contracts/customers/${contractID}/terminate`,
        data
    )
    return response?.data
}

export const updateContractRegistrationDate = async ({
    contractID,
    data,
}: {
    contractID: number
    data: {
        registration_date: string | null
    }
}) => {
    const response = await datacentral.put<ResponseObject<CustomerContract>>(
        `/contracts/customers/${contractID}/registration_date`,
        data
    )
    return response?.data
}

// suggestion sample contract
export const getSuggestionSampleContract = async ({
    contractID,
}: {
    contractID: number
}) => {
    const response = await datacentral.get<ResponseArray<SampleContract>>(
        `/contracts/customers/${contractID}/suggestions`
    )
    return response?.data || []
}

/**
 * Export overdue contracts to Excel file
 * Downloads all contracts that have passed their expiry date
 */
export const exportOverdueContracts = async (): Promise<void> => {
    const response = await datacentral.get(
        '/contracts/customers/overdue/export',
        {
            responseType: 'blob',
        }
    )

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition']
    let filename = `hop-dong-qua-han_${new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`

    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/)
        if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1]
        }
    }

    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
}
